'use client';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import React, { HTMLAttributeAnchorTarget } from 'react';
import { createPortal } from 'react-dom';

import { checkActivePathname } from '@/helpers/active-link-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import { DROPDOWN_MENU_ITEM_CLASSNAME } from '@cineamo/ui/src/dropdown/dropdown-colors';
import DropdownPanel from '@cineamo/ui/src/dropdown/DropdownPanel';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link, usePathname } from '@/i18n/routing';

export type NavigationDropdownItem = {
    href: string;
    title: string;
    icon: React.ReactNode;
    /** Analytics id, reported as `Navigation Item Clicked { item }` like a top-level nav item. */
    item?: string;
    target?: HTMLAttributeAnchorTarget;
    rel?: string;
};

type NavigationDropdownMobileProps = {
    icon: React.ReactNode;
    activeIcon?: React.ReactNode;
    triggerTitle: string;
    items: NavigationDropdownItem[];
    /**
     * Paths whose pages this dropdown stands in for. The trigger then highlights like a normal
     * nav item while one of them is open, so collapsing entries into a dropdown does not lose
     * the "you are here" cue.
     */
    routes?: string[];
};

/**
 * Bottom-navigation entry that opens a list of links instead of navigating.
 *
 * Used wherever the mobile tab row has more entries than fit legibly — planMobileNavigation caps
 * the row at MAX_MOBILE_NAV_ITEMS, and the overflow is grouped behind one of these.
 */
function NavigationDropdownMobile(props: NavigationDropdownMobileProps): React.JSX.Element {
    const { icon, activeIcon, triggerTitle, items, routes } = props;
    const isMounted = useIsMounted();
    const { track } = useAnalytics();
    const currentPath = usePathname();

    const isActive = routes?.some((route) => checkActivePathname(route, currentPath)) ?? false;

    return (
        // Width comes from the equal grid column in NavigationMobile, matching NavigationItemMobile.
        <Popover className="min-w-0">
            {({ open, close }) => (
                <>
                    <PopoverButton
                        className={cn(
                            'w-full',
                            open || isActive
                                ? 'text-[var(--on-background-color)]'
                                : 'text-[rgba(var(--on-background-color-rgb),0.8)]'
                        )}>
                        <div className="relative flex flex-col items-center space-y-[3px] pt-[6px]">
                            <div className="flex-none">{isActive && activeIcon ? activeIcon : icon}</div>
                            <span className="text-body-bold-10 leading-[16px]">{triggerTitle}</span>
                        </div>
                    </PopoverButton>
                    {/* Dim the rest of the page (but not the navigation or the panel) while open.
                     *  Rendered into the body below the bottom navigation (z-navigation = 49) but
                     *  above the page content, so the navigation bar and the panel stay in focus. */}
                    {open &&
                        isMounted &&
                        createPortal(
                            <div
                                className="fixed inset-0 z-[48] bg-black/60"
                                onClick={() => close()}
                            />,
                            document.body
                        )}
                    <DropdownPanel
                        as={PopoverPanel}
                        anchor="top"
                        className={cn('w-full z-[70] rounded-8', '[--anchor-gap:8px]')}>
                        {items.map((item) => (
                            <Link
                                key={item.href}
                                href={item.href}
                                target={item.target}
                                rel={item.rel}
                                onClick={() => {
                                    if (item.item) {
                                        track('Navigation Item Clicked', { item: item.item });
                                    }
                                    close();
                                }}
                                className={cn(
                                    'flex flex-row items-center space-x-12 py-16 px-16 text-body-14 w-full',
                                    DROPDOWN_MENU_ITEM_CLASSNAME
                                )}>
                                {item.icon}
                                <span>{item.title}</span>
                            </Link>
                        ))}
                    </DropdownPanel>
                </>
            )}
        </Popover>
    );
}

export default NavigationDropdownMobile;
